import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  message,
} from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { ColumnsType } from "antd/es/table";
import { useGetClients, webhookService } from "~/services";
import { Webhook, WebhookEventType, WebhookStatus } from "~/types/webhook";
import { PaginatedResponse } from "~/types/common";

const WebhooksScreen: React.FC = () => {
  const [webhooks, setWebhooks] = useState<Webhook[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingWebhook, setEditingWebhook] = useState<Webhook | null>(null);
  const [form] = Form.useForm();
  const [quickbooksEnabled, setQuickbooksEnabled] = useState(false);

  const { data: clients } = useGetClients({ params: { getAll: true } });

  const fetchWebhooks = async (page = 1, limit = 10) => {
    setLoading(true);
    try {
      const response: PaginatedResponse<Webhook> =
        await webhookService.getWebhooks({
          page,
          limit,
        });
      setWebhooks(response.data);
      setPagination({
        current: response.page,
        pageSize: response.limit,
        total: response.total,
      });
    } catch (error) {
      message.error("Failed to fetch webhooks");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWebhooks();
  }, []);

  const handleTableChange = (pagination: any) => {
    fetchWebhooks(pagination.current, pagination.pageSize);
  };

  const showModal = (webhook?: Webhook) => {
    if (webhook) {
      setEditingWebhook(webhook);
      setQuickbooksEnabled(webhook.quickbooksEnabled);
      form.setFieldsValue({
        ...webhook,
        events: webhook.events,
      });
    } else {
      setEditingWebhook(null);
      setQuickbooksEnabled(false);
      form.resetFields();
    }
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      if (editingWebhook) {
        await webhookService.updateWebhook(editingWebhook.id, values);
        message.success("Webhook updated successfully");
      } else {
        await webhookService.createWebhook(values);
        message.success("Webhook created successfully");
      }

      setIsModalVisible(false);
      form.resetFields();
      fetchWebhooks(pagination.current, pagination.pageSize);
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await webhookService.deleteWebhook(id);
      message.success("Webhook deleted successfully");
      fetchWebhooks(pagination.current, pagination.pageSize);
    } catch (error) {
      message.error("Failed to delete webhook");
      console.error(error);
    }
  };

  const columns: ColumnsType<Webhook> = [
    {
      title: "URL",
      dataIndex: "url",
      key: "url",
      ellipsis: true,
    },
    {
      title: "Client",
      dataIndex: ["client", "clientRef"],
      key: "client",
      render: (text, record) => text || record.clientId,
    },
    {
      title: "Events",
      dataIndex: "events",
      key: "events",
      render: (events: WebhookEventType[]) => (
        <>
          {events.map((event) => {
            let color = "";
            switch (event) {
              case WebhookEventType.PAYMENT_RECEIVED:
                color = "green";
                break;
              case WebhookEventType.INVOICE_CREATED:
                color = "blue";
                break;
              case WebhookEventType.INVOICE_SENT:
                color = "purple";
                break;
              case WebhookEventType.INVOICE_OVERDUE:
                color = "red";
                break;
            }
            return (
              <Tag color={color} key={event}>
                {event.replace("_", " ").toUpperCase()}
              </Tag>
            );
          })}
        </>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: WebhookStatus) => (
        <Tag color={status === WebhookStatus.ACTIVE ? "green" : "red"}>
          {status.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: "QuickBooks",
      dataIndex: "quickbooksEnabled",
      key: "quickbooksEnabled",
      render: (enabled: boolean) => (
        <Tag color={enabled ? "blue" : "default"}>
          {enabled ? "ENABLED" : "DISABLED"}
        </Tag>
      ),
    },
    {
      title: "Last Notification",
      dataIndex: "lastNotificationTime",
      key: "lastNotificationTime",
      render: (time: string) =>
        time ? new Date(time).toLocaleString() : "N/A",
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            onClick={() => showModal(record)}
            type="primary"
            size="small"
          />
          <Popconfirm
            title="Are you sure you want to delete this webhook?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button icon={<DeleteOutlined />} danger size="small" />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const eventOptions = [
    { label: "Payment Received", value: WebhookEventType.PAYMENT_RECEIVED },
    { label: "Invoice Created", value: WebhookEventType.INVOICE_CREATED },
    { label: "Invoice Sent", value: WebhookEventType.INVOICE_SENT },
    { label: "Invoice Overdue", value: WebhookEventType.INVOICE_OVERDUE },
  ];

  const statusOptions = [
    { label: "Active", value: WebhookStatus.ACTIVE },
    { label: "Inactive", value: WebhookStatus.INACTIVE },
  ];

  return (
    <div>
      <Card
        title="Webhooks Management"
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => showModal()}
          >
            Add Webhook
          </Button>
        }
      >
        <Table
          columns={columns}
          dataSource={webhooks}
          rowKey="id"
          pagination={pagination}
          loading={loading}
          onChange={handleTableChange}
        />
      </Card>

      <Modal
        title={editingWebhook ? "Edit Webhook" : "Create Webhook"}
        open={isModalVisible}
        onCancel={handleCancel}
        onOk={handleSubmit}
        width={700}
      >
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="url"
                label="Webhook URL"
                rules={[
                  { required: true, message: "Please enter the webhook URL" },
                  { type: "url", message: "Please enter a valid URL" },
                ]}
              >
                <Input placeholder="https://example.com/webhook" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="clientId"
                label="Client"
                rules={[{ required: true, message: "Please select a client" }]}
              >
                <Select
                  loading={loading}
                  placeholder="Select a client"
                  options={clients?.items.map((client) => ({
                    label: client.companyName,
                    value: client.id,
                  }))}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="events"
                label="Events"
                rules={[{ required: true, message: "Please select events" }]}
              >
                <Select
                  mode="multiple"
                  placeholder="Select events"
                  options={eventOptions}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="secret" label="Secret Key">
                <Input.Password placeholder="Secret key for signature verification" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="status"
                label="Status"
                initialValue={WebhookStatus.ACTIVE}
              >
                <Select options={statusOptions} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="quickbooksEnabled"
                label="QuickBooks Integration"
                valuePropName="checked"
              >
                <Switch onChange={(checked) => setQuickbooksEnabled(checked)} />
              </Form.Item>
            </Col>
          </Row>

          {quickbooksEnabled && (
            <>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="quickbooksRealmId"
                    label="QuickBooks Realm ID"
                    rules={[
                      {
                        required: quickbooksEnabled,
                        message: "Please enter QuickBooks Realm ID",
                      },
                    ]}
                  >
                    <Input placeholder="QuickBooks Realm ID" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="quickbooksOauthToken"
                    label="QuickBooks OAuth Token"
                    rules={[
                      {
                        required: quickbooksEnabled,
                        message: "Please enter QuickBooks OAuth Token",
                      },
                    ]}
                  >
                    <Input.Password placeholder="QuickBooks OAuth Token" />
                  </Form.Item>
                </Col>
              </Row>
            </>
          )}
        </Form>
      </Modal>
    </div>
  );
};

export default WebhooksScreen;
